import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Button } from "../components/ui";
import { familyService } from "../services/familyService";
import { fileUrl } from "../config/api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "form", label: "Forms" },
  { id: "doc", label: "Docs" },
  { id: "tool", label: "Tools" },
];
const typeLabel = { form: "Form", doc: "Document", tool: "Tool" };

const isHandbookDoc = (title = "") => /parent handbook|handbook/i.test(title);

export default function FormsHub() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [docs, setDocs] = useState([]);
  const [settings, setSettings] = useState({});
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selId, setSelId] = useState(null);

  useEffect(() => {
    familyService.getForms().then((r) => setForms(r?.data || [])).catch(() => {});
    familyService.getDocuments().then((r) => setDocs(r?.data || [])).catch(() => {});
    familyService.getPortalSettings().then((r) => setSettings(r?.data || {})).catch(() => {});
  }, []);

  const items = useMemo(() => {
    const f = forms.map((x) => ({
      id: "form-" + x.code, type: "form", code: x.code, icon: x.icon || "forms",
      title: x.name, blurb: x.description, meta: x.est_time,
    }));
    const d = docs.map((x) => ({
      id: "doc-" + x.id, type: "doc", icon: isHandbookDoc(x.title) ? "book" : "doc",
      title: x.title, blurb: x.description, meta: x.file_label, file: x.file_path,
      handbook: isHandbookDoc(x.title),
    }));
    const tools = settings.ccs_calculator_url
      ? [{ id: "tool-ccs", type: "tool", icon: "calc", title: "CCS Calculator",
          blurb: "Estimate your Child Care Subsidy and out-of-pocket gap fee per day.",
          meta: "Interactive", url: settings.ccs_calculator_url }]
      : [];
    return [...f, ...d, ...tools];
  }, [forms, docs, settings]);

  const list = items.filter((i) =>
    (filter === "all" || i.type === filter) &&
    (query === "" || i.title.toLowerCase().includes(query.toLowerCase()))
  );
  const sel = items.find((i) => i.id === selId) || list[0];
  const docPdfUrl = sel?.type === "doc"
    ? (sel.file ? fileUrl(sel.file) : (sel.handbook && settings.handbook_pdf ? fileUrl(settings.handbook_pdf) : null))
    : null;

  return (
    <div className="workspace forms-ws">
      <aside className="ws-side forms-side">
        <p className="eyebrow eyebrow--gap">Forms &amp; requests</p>
        <div className="search-box">
          <Icon name="search" className="ico-17 ico-soft" />
          <input placeholder="Search forms & documents" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="filter-tabs">
          {FILTERS.map((f) => (
            <button key={f.id} className={"filter-tab" + (filter === f.id ? " is-active" : "")} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="forms-list scroll-area">
          {list.length === 0 && <p className="muted fli-empty">Nothing matches that search.</p>}
          {list.map((i) => (
            <button key={i.id} className={"forms-list-item" + (sel && sel.id === i.id ? " is-active" : "")} onClick={() => setSelId(i.id)}>
              <span className="icon-circle soft sm"><Icon name={i.icon} /></span>
              <div className="detail-main">
                <div className="fli-title">{i.title}</div>
                <div className="fli-type">{typeLabel[i.type]}</div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <div className="ws-main forms-detail-wrap scroll-area">
        {sel && (
          <div className="detail-panel fade-in" key={sel.id}>
            <span className="icon-circle lg"><Icon name={sel.icon} /></span>
            <p className="eyebrow mt-22">{typeLabel[sel.type]}{sel.meta ? ` · ${sel.meta}` : ""}</p>
            <h1 className="mt-8">{sel.title}</h1>
            <p className="detail-blurb">{sel.blurb}</p>

            <div className="detail-actions">
              {sel.type === "form" && (
                <Button variant="primary" icon="edit" onClick={() => navigate("/forms/" + sel.code)}>Open form</Button>
              )}
              {sel.type === "doc" && (
                <>
                  {sel.handbook ? (
                    <Button variant="primary" icon="external" onClick={() => navigate("/handbook")}>Open</Button>
                  ) : docPdfUrl ? (
                    <a href={docPdfUrl} target="_blank" rel="noreferrer">
                      <Button variant="primary" icon="external">Open</Button>
                    </a>
                  ) : (
                    <Button variant="primary" icon="external" disabled>Open</Button>
                  )}
                  {docPdfUrl ? (
                    <a href={docPdfUrl} target="_blank" rel="noreferrer">
                      <Button variant="ghost" icon="download">Download PDF</Button>
                    </a>
                  ) : (
                    <Button variant="ghost" icon="download" disabled>Download PDF</Button>
                  )}
                </>
              )}
              {sel.type === "tool" && (
                <a href={sel.url} target="_blank" rel="noreferrer"><Button variant="primary" icon="external">Open calculator</Button></a>
              )}
            </div>

            {sel.type === "form" && (
              <div className="detail-note">
                <Icon name="info" className="ico-18 ico-orange" />
                <span>Forms open blank for you to fill in. When you're done, choose <strong>“Save and send to your centre”</strong> and your centre team takes it from there.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
